import { useEffect, useRef, useState } from "react";
import { Sparkle, X, PaperPlaneRight, Trash, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * TrainerAssistantBubble — floating AI chat bubble for the trainer.
 *
 * Iter 60.1 — replaces the previous full-page /trainer/assistant tab. Now lives
 * as a bottom-right popup (Whoop-style) mounted globally inside TrainerLayout so
 * the coach can ask business questions from any page without navigating.
 *
 * Behaviour:
 *   - Collapsed: circular Sparkle button, bottom-right.
 *   - Expanded: 400x600 chat panel with history, empty-state suggestions,
 *     composer, typing indicator, and a Clear button.
 *   - History persists in the DB (`/trainer-bot/history`) so opening/closing
 *     the bubble keeps context.
 */
export default function TrainerAssistantBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        api.get("/trainer-bot/history"),
        api.get("/trainer-bot/suggestions"),
      ]);
      const msgs = h.data?.messages || [];
      setMessages(msgs);
      setSuggestions(s.data?.suggestions || []);
      setHasHistory(msgs.length > 0);
    } catch (_e) {
      // Silent — the bubble stays usable, just no prior history / suggestions
    } finally {
      setLoading(false);
    }
  };

  // Prime the unread-history badge on mount so the dot on the closed bubble
  // reflects any existing conversation.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/trainer-bot/history?limit=1");
        setHasHistory((data?.messages || []).length > 0);
      } catch (_e) {}
    })();
  }, []);

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput("");
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: value,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);
    try {
      const { data } = await api.post("/trainer-bot/chat", { message: value });
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimistic.id),
        data.user_message,
        data.assistant_message,
      ]);
      setHasHistory(true);
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      toast.error(err.response?.data?.detail || "Assistant is unavailable — try again");
    } finally {
      setSending(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear this entire conversation?")) return;
    try {
      await api.delete("/trainer-bot/history");
      setMessages([]);
      setHasHistory(false);
      toast.success("Conversation cleared");
    } catch (_e) {
      toast.error("Could not clear");
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-brand hover:bg-brand-light text-white shadow-2xl flex items-center justify-center transition-all hover:scale-105"
          data-testid="trainer-assistant-toggle"
          aria-label="Open AI assistant"
        >
          <Sparkle size={22} weight="fill" />
          {hasHistory && (
            <span
              className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0A0A0A]"
              aria-hidden
              data-testid="trainer-assistant-badge"
            />
          )}
        </button>
      )}

      {/* Expanded panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-full max-w-[420px] card-flat flex flex-col shadow-2xl"
          style={{ height: "min(620px, 85vh)" }}
          data-testid="trainer-assistant-panel"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 bg-brand/15 border border-brand/40 flex items-center justify-center shrink-0">
                <Sparkle size={16} weight="duotone" className="text-brand-light" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg leading-none">Traina Insights</div>
                <div className="tracker text-zinc-500 mt-1">Ask about your business</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="p-2 text-zinc-500 hover:text-white"
                  data-testid="trainer-assistant-clear"
                  title="Clear conversation"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-zinc-400 hover:text-white"
                data-testid="trainer-assistant-close"
                aria-label="Close assistant"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Thread */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            data-testid="trainer-assistant-thread"
          >
            {loading ? (
              <div className="tracker text-zinc-500 text-center py-8 text-[10px]">Loading…</div>
            ) : messages.length === 0 ? (
              <EmptyState suggestions={suggestions} onPick={send} />
            ) : (
              <>
                {messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} />
                ))}
                {sending && <TypingIndicator />}
              </>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="px-4 py-3 border-t border-white/10 flex items-end gap-2 shrink-0"
            data-testid="trainer-assistant-form"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask about income, clients, no-shows…"
              className="flex-1 px-3 py-2 border bg-[#0A0A0A] resize-none min-h-[42px] max-h-24 text-sm"
              rows={1}
              disabled={sending}
              data-testid="trainer-assistant-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="btn-primary !py-2 !px-3 disabled:opacity-40 shrink-0"
              data-testid="trainer-assistant-send"
              aria-label="Send"
            >
              <PaperPlaneRight size={14} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function EmptyState({ suggestions, onPick }) {
  return (
    <div className="py-6 text-center" data-testid="trainer-assistant-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand/15 mb-3">
        <ChatCircleDots size={22} weight="duotone" className="text-brand-light" />
      </div>
      <h3 className="font-display text-lg mb-1">What do you want to know?</h3>
      <p className="text-xs text-zinc-500 max-w-xs mx-auto mb-5">
        Try one of these or type your own question.
      </p>
      <div className="flex flex-col gap-2">
        {suggestions.slice(0, 5).map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            className="text-left px-3 py-2 border border-white/10 hover:border-brand hover:bg-brand/5 transition-colors text-xs"
            data-testid={`trainer-assistant-suggestion-${i}`}
          >
            <Sparkle size={10} weight="bold" className="inline mr-1.5 text-brand-light" />
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`trainer-assistant-msg-${msg.role}`}
    >
      <div
        className={`max-w-[85%] px-3 py-2 whitespace-pre-wrap text-sm leading-relaxed ${
          isUser
            ? "bg-brand text-white"
            : "bg-[#141414] border border-white/10 text-zinc-200"
        }`}
      >
        {!isUser && (
          <div className="tracker text-[9px] text-brand-light mb-1 flex items-center gap-1">
            <Sparkle size={9} weight="bold" /> Traina Insights
          </div>
        )}
        {renderContent(msg.content)}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start" data-testid="trainer-assistant-typing">
      <div className="max-w-[85%] px-3 py-2 bg-[#141414] border border-white/10">
        <div className="tracker text-[9px] text-brand-light mb-1 flex items-center gap-1">
          <Sparkle size={9} weight="bold" /> Traina Insights
        </div>
        <div className="flex gap-1.5 items-center">
          <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

// Minimal markdown-ish rendering — bolds only. Keep it lightweight.
function renderContent(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}
